import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router-dom';
import Footer from '../../components/Footer';
import 'wicg-inert';
import ScrollToTop from "../../libs/scrollToTop";
import { useKonami } from 'react-konami-code';
import Nav from '../../components/Nav.js';
import SecondaryNav from '../../components/SecondaryNav';

import ModalOverlay from "../../components/ModalOverlay";

import heroMobileImage from '../../images/articles/PO0131005_WF_BRAND_2HFY24_ISF_NovUpdate16_LP_767x577.jpg';
import heroDesktopImage from '../../images/articles/PO0131005_WF_BRAND_2HFY24_ISF_NovUpdate16_LP_1164x584.jpg';

import { fireTag } from '../../libs/analytics.js';

const EVInvestment = ({
    handleEvent,
    closeExitOverlay,
    addExitClickListeners,
    modalOverlay,
}) => {

    const [enableScrollGA, setEnableScrollGA] = useState(false);
    const [shouldBeInert, setShouldBeInert] = useState(false);
    const [gridVisible, setGridVisible] = useState(false);
    const heroOGImage = 'PO0131005_WF_BRAND_2HFY24_ISF_NovUpdate16_LP_ogimage.jpg';

    const toggleInert = () => {
        setShouldBeInert(!shouldBeInert)
    }

    const linkDisclosures = () => {
		const bodyDisclosures = document.querySelectorAll('.body-disclosures');
		const footerDisclosures = document.querySelectorAll('.footer-disclosures');
		bodyDisclosures.forEach(elem => {
			elem.addEventListener('click', (e) => {
				e.preventDefault();
				let disclosureNumber = Number(e.target.innerHTML)
				let targetElement = footerDisclosures[disclosureNumber - 1];
				window.scrollTo({
					left: 0,
					top: targetElement.offsetTop,
					behavior: 'smooth'
				});
				window.setTimeout(() => {
					targetElement.focus();
				}, 1000);
			});
		});

		footerDisclosures.forEach(elem => {
			elem.addEventListener('click', (e) => {
				e.preventDefault();
				let disclosureNumber = Number(e.target.innerHTML)
				let targetElement = bodyDisclosures[disclosureNumber - 1];
				fireTag('A2',disclosureNumber);
				window.scrollTo({
					left: 0,
					top: targetElement.offsetTop,
					behavior: 'smooth'
				});
				window.setTimeout(() => {
					targetElement.focus();
				}, 1000);
			});
		});
	}

	useEffect(() => {
		linkDisclosures();
		addExitClickListeners();
	}, []); // empty array says only use this effect once

    let mastheadImage = window.innerWidth < 768 ? heroMobileImage : heroDesktopImage;

    return (
		<>
			<Helmet>
				<title>Two years of EV investments spark key economic impacts</title>
				<meta
					name="description"
					content="The Inflation Reduction Act’s electric vehicle incentives drive manufacturing, job growth, and consumer savings."
				/>
				<meta property="og:title" content="Two years of EV investments spark key economic impacts"/>
				<meta
					property="og:description"
					content="The Inflation Reduction Act’s electric vehicle incentives drive manufacturing, job growth, and consumer savings."
				/>
				<meta property="og:image" content={`https://welcome.wf.com/impact/images/articles/${heroOGImage}`}/>

				<meta property="twitter:title" content="Two years of EV investments spark key economic impacts"/>
				<meta
					property="twitter:description"
					content="The Inflation Reduction Act’s electric vehicle incentives drive manufacturing, job growth, and consumer savings."
				/>
				i
			</Helmet>
			{/*TODO: isMobile should be passed in from route, not calc'd here */}

			<Nav handleEvent={handleEvent} isNotHomePage={false} activePage="articles" isMobile={window.innerWidth < 768} showExternalNav toggleInert={toggleInert} shouldBeInert={shouldBeInert}/>

			{!window.innerWidth < 768 && (
				<SecondaryNav page="Articles" shouldBeInert={shouldBeInert} activePage="articles"/>
			)}


			<main className="flex-container">

				<div className="flex-row">
					<div className="flex-col-sm-12 flex-col-md-8 text-center headline">
						<h1 id="articles-main-h1" style={{marginBottom: 10}}>
                        Two years of EV investments spark key economic impacts
                        </h1>
						<h2 className="summary">
                        The Inflation Reduction Act’s electric vehicle incentives drive manufacturing, job growth, and consumer savings. 
                        </h2>
					</div>

				</div>

				<div className="flex-row full-width-md">
					<div className="flex-col-lg-10 flex-col-md-10 text-center hero-container">
						<img src={mastheadImage} alt="Driver inserts charger into electric vehicle."/>
					</div>
				</div>
                
				<div className="flex-row">
					<div className="flex-col-md-8 flex-col-sm-12 text-center article-intro info-graphic">
                    <div className="by-line">
							<p>By Jeffrey Schub, <span>sustainable finance integration leader at Wells Fargo</span></p>
						</div>
						<div className="article-info">
							<div>
								<p>November 14, 2024</p>
							</div>
							<div>
								<p>4-minute read</p>
							</div>
						</div>
						<p>The Inflation Reduction Act, or IRA, is a pivotal policy to accelerate the clean energy transition. By 2030, the IRA is expected to help reduce U.S. emissions by as much as 41% from 2005 levels. Today, two years after the landmark legislation was passed, the full emissions impact remains to be seen, but there are clear economic impacts.</p>
                        <p>
                        In the electric vehicle (EV) sector specifically, the IRA has influenced three key areas — manufacturing, jobs, and consumer savings. These are evident through investments across the EV supply chain.
						</p>
                        <h2 className="subhead" style={{marginTop: '1rem'}}>
                        Manufacturing boom hits the EV industry  						
                        </h2>
						<p>
                        The IRA incentivized domestic manufacturing of EVs, which has led to increased production. A <a onClick={()=> fireTag('A1','report by clean investment monitor')} target="_blank" href="https://cdn.prod.website-files.com/64e31ae6c5fd44b10ff405a7/66b2bf45bd0dd034beefb5bd_Clean%20Investment%20Monitor_Tallying%20the%20Two-Year%20Impact%20of%20the%20Inflation%20Reduction%20Act.pdf" rel="noreferrer">report by Clean Investment Monitor</a>, which tracks public and private investments in cleantech, finds most — almost 80% — of the $133 billion in clean energy manufacturing announcements have been in the EV supply chain. Since the IRA was passed, investments in EV assembly grew 45%, while investments in batteries, fueling equipment, and critical minerals refining and processing tripled.                         </p>
                        <p>
                        U.S. automakers are tapping into the manufacturing boom by expanding their fleets. Tesla, Rivian, Ford, and General Motors have <a onClick={()=> fireTag('A1','announced new electric models')} target="_blank" href="https://www.cnbc.com/2024/09/07/tesla-gm-rivian-and-ford-the-100000-electric-truck-market-is-here.html" rel="noreferrer">announced new electric models</a>, including pickup trucks and SUVs, that appeal to a wider range of customers.</p>
					<p>
                    The IRA also supports manufacturers who want to build batteries for EVs. Battery manufacturers can receive <a onClick={()=> fireTag('A1','production credits')} target="_blank" href="https://www.iea.org/policies/16282-inflation-reduction-act-2022-sec-13502-advanced-manufacturing-production-credit" rel="noreferrer">production credits</a> for U.S.-produced battery cells and battery modules. According to the <a onClick={()=> fireTag('A1','environmental defense fund')} target="_blank" href="https://www.edf.org/media/analysis-finds-us-electric-vehicle-battery-manufacturing-track-meet-demand" rel="noreferrer">Environmental Defense Fund</a>, announced EV battery manufacturing could supply more than 1,000 gigawatt hours per year by 2028 to meet the growing demand. 
                    </p>
                    <h2 className="subhead" style={{marginTop: '1rem'}}>
                    New jobs emerge in the EV supply chain                      
                    </h2>
                    <p>
                    With the boost in manufacturing, there is a growing need for skilled workers. In the first 18 months of the IRA, manufacturers announced <a onClick={()=> fireTag('A1','99,600 new ev-related jobs')} target="_blank" href="https://www.edf.org/media/us-electric-vehicle-investments-have-grown-188-billion-almost-200000-jobs-new-report" rel="noreferrer">99,600 new EV-related jobs</a>. Most of these jobs were added in the batteries sector.
					</p>
                    <p>
                    The United Auto Workers (UAW) ratified a <a onClick={()=> fireTag('A1','new agreement')} target="_blank" href="https://uaw.org/uaw-members-ratify-historic-contracts-at-ford-gm-and-stellantis/" rel="noreferrer">new agreement</a> with three automakers — Ford, Stellantis, and General Motors — to bring thousands of EV and battery jobs under the union’s national agreement. The deal allows UAW to unionize EV battery plants to secure fair wages and benefits. The surge in battery manufacturing is driving demand for <a onClick={()=> fireTag('A1','technical roles')} target="_blank" href="https://www.wri.org/insights/ev-transition-auto-manufacturing-jobs" rel="noreferrer">technical roles</a>, like engineers and quality control experts.                      
                    </p>
                    <h2 className="subhead" style={{marginTop: '1rem'}}>
                    Big money-saving opportunities for drivers                     
                    </h2>
                    <p>
                    An <a onClick={()=> fireTag('A1','international energy agency report')} target="_blank" href="https://www.iea.org/reports/global-ev-outlook-2024/trends-in-electric-cars" rel="noreferrer">International Energy Agency report</a> found the IRA incentives appear to have supported EV sales last year.  In 2023, <a onClick={()=> fireTag('A1','more than 1.6 million')} target="_blank" href="https://www.marketwatch.com/guides/insurance-services/electric-vehicle-statistics-2024/" rel="noreferrer">more than 1.6 million</a> EVs were sold in the U.S. — a 60% increase from 2022.  
                    </p>
                    <p>
                    The IRA includes up to <a onClick={()=> fireTag('A1','$7,500 in tax credits')} target="_blank" href="https://www.irs.gov/credits-deductions/credits-for-new-clean-vehicles-purchased-in-2023-or-after" rel="noreferrer">$7,500 in tax credits</a> for consumers purchasing new EVs that meet specific domestic content requirements. In the first three months of 2024, buyers <a onClick={()=> fireTag('A1','collectively saved')} target="_blank" href="https://home.treasury.gov/news/featured-stories/after-just-three-months-the-inflation-reduction-act-ira-has-saved-americans-an-estimated-600-million-on-clean-vehicle-purchases-at-the-time-of-sale#_ftn1" rel="noreferrer">collectively saved</a> over $600 million at the time-of-sale in tax credits, an average of $6,900 per sale. These savings combined with potential fuel and maintenance savings are making EVs more practical for drivers. The <a onClick={()=> fireTag('A1','tax credit for used evs')} target="_blank" href="https://www.irs.gov/credits-deductions/used-clean-vehicle-credit" rel="noreferrer">tax credit for used EVs</a> — up to $4,000 — is also helping to expand accessibility and market growth as the used EV inventory increases.   
                    </p>
                    <p>
                    The long-term effects of the IRA are beginning to unfold as both manufacturers and consumers see the financial benefits of increased incentives. As manufacturing scales up, new jobs are created, and more consumers buy EVs, there may be broader economic growth, innovation, and sustainability impacts. 
                    </p>
                    </div>
				</div>
			</main>


			<Footer id="footer" footerStyles="global" shouldBeInert={shouldBeInert} activePage="sustainability" enableScrollGA={enableScrollGA}/>

			{modalOverlay && modalOverlay.open && (
				<ModalOverlay closeExitOverlay={closeExitOverlay} modalOverlay={modalOverlay}/>
			)}
		</>
    );

};

export default withRouter(EVInvestment);


